import React, { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Chip, IconButton, TablePagination, FormControl, InputLabel, Select, MenuItem, TextField, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from 'react-redux';
import { HousekeepingService } from '../../api/services/housekeepingService';
import { StaffService } from '../../api/services/staffService';
import TaskFormModal from '../../components/TaskFormModal';
import TaskDetailsModal from '../../components/TaskDetailsModal';
import ReplayIcon from '@mui/icons-material/Replay';
import { extractTotalCount, standardFilterContainerSx, standardFilterSelectSx, standardFilterInputSx, standardResetButtonSx } from '../../components/common/tableStyles';

export default function LiveTasksTab({ tenantId, onTaskSuccess, refreshKey }) {
    const { user } = useSelector(state => state.auth);

    const userPermissions = useMemo(() => {
        if (!user) return [];
        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        return rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') {
                return p.key || p.name || p.code || '';
            }
            return '';
        }).filter(Boolean);
    }, [user]);

    const canManage = userPermissions.includes('*') || userPermissions.includes('CAN_MANAGE_HOUSEKEEPING');
    const canComplete = userPermissions.includes('*') || userPermissions.includes('CAN_COMPLETE_HS_TASK') || canManage;

    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [taskFilters, setTaskFilters] = useState({ status: '', priority: '', staffMember: '', date: '' });
    const [staffList, setStaffList] = useState([]);
    
    const [taskModal, setTaskModal] = useState({ open: false, data: null });
    const [detailsModal, setDetailsModal] = useState({ open: false, taskId: null });

    useEffect(() => {
        const fetchStaff = async () => {
            if (!canManage) return;
            try {
                const res = await StaffService.getStaff({ noPagination: true });
                const list = Array.isArray(res)
                    ? res
                    : (Array.isArray(res?.data)
                        ? res.data
                        : (Array.isArray(res?.staff)
                            ? res.staff
                            : (Array.isArray(res?.data?.staff)
                                ? res.data.staff
                                : (Array.isArray(res?.staffMembers)
                                    ? res.staffMembers
                                    : (Array.isArray(res?.data?.staffMembers)
                                        ? res.data.staffMembers
                                        : [])))));
                setStaffList(list.filter(s => !s.status || s.status.toLowerCase() === 'active'));
            } catch (error) {
                console.error("Failed to fetch staff list:", error);
            }
        };
        fetchStaff();
    }, [tenantId, canManage]);

    // Build complete list of staff including any already assigned to tasks
    const displayedStaff = [...staffList];
    tasks.forEach(t => {
        const staffObj = t.assignedStaff;
        const staffName = staffObj?.fullName || staffObj?.name || (typeof t.assignedTo === 'string' ? t.assignedTo : '') || t.assignedStaffName;
        const staffId = staffObj?.id || staffObj?._id || t.assignedStaffId || staffName;
        if (staffName && !displayedStaff.some(s => (s.id && s.id === staffId) || (s.fullName || s.name) === staffName)) {
            displayedStaff.push({
                id: staffId,
                fullName: staffName,
                name: staffName,
            });
        }
    });

    const fetchTasks = async () => {
        setIsLoading(true);
        try {
            const selectedStaff = displayedStaff.find(s => (s.id || s._id || s.name || s.fullName) === taskFilters.staffMember);
            const staffName = selectedStaff?.fullName || selectedStaff?.name || taskFilters.staffMember;
            const staffId = selectedStaff?.id || selectedStaff?._id || taskFilters.staffMember;

            const params = {
                page,
                limit: rowsPerPage,
                status: taskFilters.status || undefined,
                priority: taskFilters.priority || undefined,
                staffMember: staffName || undefined,
                assignedStaffId: staffId || undefined,
                date: taskFilters.date || undefined,
            };
            const res = await HousekeepingService.getAllTasks(tenantId, params);
            const apiTasks = res?.data?.tasks || res?.tasks || res?.data || res || [];
            setTasks(Array.isArray(apiTasks) ? apiTasks : []);
            setTotal(extractTotalCount(res, apiTasks));
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, [tenantId, page, rowsPerPage, refreshKey, taskFilters]);

    const handleSuccess = () => {
        fetchTasks();
        if (onTaskSuccess) onTaskSuccess();
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleTaskFilterChange = (e) => {
        setTaskFilters({ ...taskFilters, [e.target.name]: e.target.value });
        setPage(0);
    };

    const getPriorityStyle = (priority) => {
        switch (priority) {
            case 'Urgent':
                return { bgcolor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' };
            case 'High':
                return { bgcolor: '#ffedd5', color: '#ea580c', border: '1px solid #fed7aa' };
            case 'Medium':
                return { bgcolor: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' };
            case 'Low':
                return { bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
            default:
                return { bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Completed':
                return { bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' };
            case 'In Progress':
                return { bgcolor: '#e3f2fd', color: '#1976d2', border: '1px solid #bbdefb' };
            case 'Inspection':
                return { bgcolor: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2' };
            case 'Cancelled':
                return { bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2' };
            case 'Ready':
            default:
                return { bgcolor: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' };
        }
    };

    const paginatedTasks = tasks;

    return (
        <Box>
            <Box sx={standardFilterContainerSx}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select name="status" value={taskFilters.status} displayEmpty onChange={handleTaskFilterChange} sx={standardFilterSelectSx}>
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="Ready">Ready</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Inspection">Inspection</MenuItem>
                            <MenuItem value="Completed">Completed</MenuItem>
                            <MenuItem value="Cancelled">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select name="priority" value={taskFilters.priority} displayEmpty onChange={handleTaskFilterChange} sx={standardFilterSelectSx}>
                            <MenuItem value="">All Priorities</MenuItem>
                            <MenuItem value="Urgent">Urgent</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                            name="staffMember"
                            value={taskFilters.staffMember}
                            displayEmpty
                            onChange={handleTaskFilterChange}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="">All Staff</MenuItem>
                            {displayedStaff.map((s) => {
                                const name = s.fullName || s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email || s.employeeId || 'Staff';
                                const val = s.id || s._id || s.name || s.fullName;
                                return (
                                    <MenuItem key={s.id || s._id || name} value={val}>
                                        {name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <TextField size="small" type="date" name="date" value={taskFilters.date} onChange={handleTaskFilterChange} sx={standardFilterInputSx} />
                    {(taskFilters.status || taskFilters.priority || taskFilters.staffMember || taskFilters.date) && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={() => setTaskFilters({ status: '', priority: '', staffMember: '', date: '' })}>Reset</Button>
                    )}
                </Box>
            </Box>
            
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Room</TableCell>
                            <TableCell>Task Type</TableCell>
                            <TableCell>Priority</TableCell>
                            <TableCell>Assigned To</TableCell>
                            <TableCell>Due By</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
                        ) : paginatedTasks.map((task) => (
                            <TableRow key={task.id} hover>
                                <TableCell><b>{task.roomNumber || task.room || task.roomId || '-'}</b></TableCell>
                                <TableCell>{task.taskType || task.type || task.taskTypeId || '-'}</TableCell>
                                <TableCell>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        ...getPriorityStyle(task.priority),
                                        px: 1.25,
                                        py: 0.35,
                                        borderRadius: '16px',
                                        fontWeight: '700',
                                        fontSize: '0.75rem',
                                    }}>
                                        {task.priority || 'Medium'}
                                    </Box>
                                </TableCell>
                                <TableCell>{task.assignedStaff?.fullName || task.assignedStaff?.name || task.assignedTo || task.assignedStaffId || 'Unassigned'}</TableCell>
                                <TableCell>{task.dueBy ? new Date(task.dueBy).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                                <TableCell>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        ...getStatusStyle(task.status),
                                        px: 1.25,
                                        py: 0.35,
                                        borderRadius: '16px',
                                        fontWeight: '700',
                                        fontSize: '0.75rem',
                                        textTransform: 'capitalize',
                                    }}>
                                        {task.status || 'Ready'}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" color="info" onClick={() => setDetailsModal({ open: true, taskId: task.id })}><VisibilityIcon fontSize="small" /></IconButton>
                                    {(canManage || canComplete) && (
                                        <IconButton size="small" color="primary" onClick={() => setTaskModal({ open: true, data: task })}><EditIcon fontSize="small" /></IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={tasks.length === 0 ? 0 : total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <TaskFormModal 
                open={taskModal.open} 
                onClose={() => setTaskModal({ open: false, data: null })} 
                editingTask={taskModal.data} 
                availableStaff={displayedStaff}
                onSuccess={handleSuccess} 
            />
            <TaskDetailsModal open={detailsModal.open} onClose={() => setDetailsModal({ open: false, taskId: null })} taskId={detailsModal.taskId} />
        </Box>
    );
}
